"use client"
import * as Select from '@radix-ui/react-select';
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  FileText,
  Globe,
  Search,
  Settings,
  Upload,
  X
} from 'lucide-react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ToastContainer, useToast } from '@/components/ui/ToastContainer';
import { compressImageIfNeeded } from '@/lib/utils/imageCompression';

interface ServiceMetaEditorProps {
  serviceId?: string;
}

export default function ServiceMetaEditor({ serviceId }: ServiceMetaEditorProps) {
  const router = useRouter();
  const { showToast, removeToast, toasts } = useToast();

  const [formData, setFormData] = useState({
    serviceName: '',
    slug: '',
    metaTitle: '',
    metaDescription: '',
    canonicalUrl: '',
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: '',
    schemaMarkup: '',
    status: 'Draft'
  });

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [availableSlugs, setAvailableSlugs] = useState<string[]>([]);


  // Load existing record when editing
  useEffect(() => {
    const fetchData = async () => {
      if (!serviceId) return;
      try {
        setError(null);
        const res = await fetch(`/api/admin/service-meta/${serviceId}`);
        if (!res.ok) throw new Error('Failed to load');
        const data = await res.json();
        setFormData({
          serviceName: data.serviceName || '',
          slug: data.slug || '',
          metaTitle: data.metaTitle || '',
          metaDescription: data.metaDescription || '',
          canonicalUrl: data.canonicalUrl || '',
          ogTitle: data.ogTitle || '',
          ogDescription: data.ogDescription || '',
          ogImage: data.ogImage || '',
          twitterTitle: data.twitterTitle || '',
          twitterDescription: data.twitterDescription || '',
          twitterImage: data.twitterImage || '',
          schemaMarkup: data.schemaMarkup || '',
          status: data.status || 'Draft',
        });
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load');
      }
    };
    fetchData();
  }, [serviceId]);

  // Load available slugs for dropdown
  useEffect(() => {
    const fetchSlugs = async () => {
      try {
        const res = await fetch('/api/all-slugs');
        if (!res.ok) throw new Error('Failed to load slugs');
        const data = await res.json();
        setAvailableSlugs(data.slugs || []);
      } catch (e: unknown) {
        console.error('Failed to load available slugs:', e);
        // Don't set error state for this - it's not critical for the form
      }
    };
    fetchSlugs();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };


  const generateCanonicalUrl = () => {
    const normalized = String(formData.slug || '')
      .trim()
      .replace(/^\/+/, '');
    const canonical = `https://www.makemybrandlabs.com/${normalized}`;
    setFormData(prev => ({ ...prev, canonicalUrl: canonical }));
  };

  const copyMetaToSocial = () => {
    setFormData(prev => ({
      ...prev,
      ogTitle: prev.metaTitle,
      ogDescription: prev.metaDescription,
      twitterTitle: prev.metaTitle,
      twitterDescription: prev.metaDescription
    }));
  };

  const handleOgImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.');
      showToast('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.', 'error');
      return;
    }

    setError(null);

    try {
      // Compress image if needed (will compress to below 1MB)
      const compressedFile = await compressImageIfNeeded(file, showToast);

      const formDataUpload = new FormData();
      formDataUpload.append('file', compressedFile);

      const response = await fetch('/api/upload/service-meta', {
        method: 'POST',
        body: formDataUpload,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload image');
      }

      const result = await response.json();

      setFormData(prev => ({ ...prev, ogImage: result.fileUrl }));

      // Clear the input
      if (e.target) {
        e.target.value = '';
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload image';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    }
  };

  const handleTwitterImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.');
      showToast('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.', 'error');
      return;
    }

    setError(null);

    try {
      // Compress image if needed (will compress to below 1MB)
      const compressedFile = await compressImageIfNeeded(file, showToast);

      const formDataUpload = new FormData();
      formDataUpload.append('file', compressedFile);

      const response = await fetch('/api/upload/service-meta', {
        method: 'POST',
        body: formDataUpload,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload image');
      }

      const result = await response.json();

      setFormData(prev => ({ ...prev, twitterImage: result.fileUrl }));

      // Clear the input
      if (e.target) {
        e.target.value = '';
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload image';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    }
  };

  const normalizedSlug = useMemo(() => String(formData.slug || '').trim().replace(/^\/+/, ''), [formData.slug]);

  const isValidUrl = (string: string) => {
    try {
      new URL(string);
      return true;
    } catch {
      return false;
    }
  };

  const handleSave = useCallback(async (publish: boolean) => {
    const validateForm = () => {
      const errors: string[] = [];

      if (!formData.serviceName.trim()) {
        errors.push('Service Name is required');
      }

      if (!formData.slug.trim()) {
        errors.push('URL Slug is required');
      }

      if (!formData.metaTitle.trim()) {
        errors.push('Meta Title is required');
      } else if (formData.metaTitle.length > 60) {
        errors.push('Meta Title must be 60 characters or less');
      }

      if (!formData.metaDescription.trim()) {
        errors.push('Meta Description is required');
      } else if (formData.metaDescription.length > 160) {
        errors.push('Meta Description must be 160 characters or less');
      }

      if (!formData.canonicalUrl.trim()) {
        errors.push('Canonical URL is required');
      } else if (!isValidUrl(formData.canonicalUrl)) {
        errors.push('Canonical URL must be a valid URL');
      }

      return errors;
    };

    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      // Client-side validation
      const validationErrors = validateForm();
      if (validationErrors.length > 0) {
        setError(validationErrors.join('. '));
        // Focus on first invalid field
        if (!formData.serviceName.trim()) {
          document.getElementsByName('serviceName')[0]?.focus();
        } else if (!formData.slug.trim()) {
          document.getElementsByName('slug')[0]?.focus();
        } else if (!formData.metaTitle.trim() || formData.metaTitle.length > 60) {
          document.getElementsByName('metaTitle')[0]?.focus();
        } else if (!formData.metaDescription.trim() || formData.metaDescription.length > 160) {
          document.getElementsByName('metaDescription')[0]?.focus();
        } else if (!formData.canonicalUrl.trim() || !isValidUrl(formData.canonicalUrl)) {
          document.getElementsByName('canonicalUrl')[0]?.focus();
        }
        return;
      }

      const payload = {
        ...formData,
        slug: normalizedSlug,
        status: publish ? 'Published' : formData.status || 'Draft',
      };

      const res = await fetch(serviceId ? `/api/admin/service-meta/${serviceId}` : '/api/admin/service-meta', {
        method: serviceId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || 'Save failed');
      }

      setSuccess(serviceId ? 'Updated successfully' : 'Created successfully');

      // Auto-dismiss success message after 3 seconds for draft saves
      if (!publish) {
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      }

      // Redirect to listing page after successful publish
      if (publish) {
        setTimeout(() => {
          router.push('/admin/service-meta');
        }, 1500); // Small delay to show success message
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save');
      // Auto-dismiss error message after 5 seconds
      setTimeout(() => {
        setError(null);
      }, 5000);
    } finally {
      setIsSaving(false);
    }
  }, [formData, normalizedSlug, router, serviceId]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 's') {
          e.preventDefault();
          if (e.shiftKey) {
            // Ctrl+Shift+S for publish
            handleSave(true);
          } else {
            // Ctrl+S for save draft
            handleSave(false);
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleSave]); // Use memoized handleSave

  return (
    <div className="relative">
      {isSaving && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="text-lg font-medium">Saving...</span>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Basic Service Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Settings className="h-5 w-5 mr-2 text-blue-600" />
            Basic Service Information
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service Name *
              </label>
              <input
                type="text"
                name="serviceName"
                value={formData.serviceName}
                onChange={handleInputChange}
                className={`w-full border rounded-md px-3 py-2 focus:ring-primary focus:border-primary ${!formData.serviceName.trim() && error ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="Enter service name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL Slug *
              </label>
              <Select.Root
                name="slug"
                value={formData.slug}
                onValueChange={(value) => setFormData(prev => ({ ...prev, slug: value }))}
                required
              >
                <Select.Trigger className={`w-full border rounded-md px-3 py-2 focus:ring-primary focus:border-primary bg-white text-left flex items-center justify-between ${!formData.slug.trim() && error ? 'border-red-300' : 'border-gray-300'}`}>
                  <Select.Value placeholder="Select a page slug..." />
                  <Select.Icon>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                  </Select.Icon>
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content className="bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto z-50">
                    <Select.Viewport className="p-1">
                      {availableSlugs.map((slug) => (
                        <Select.Item
                          key={slug}
                          value={slug}
                          className="px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none cursor-pointer"
                        >
                          <Select.ItemText>{slug}</Select.ItemText>
                        </Select.Item>
                      ))}
                    </Select.Viewport>
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
              <p className="mt-2 text-xs text-gray-500">Final path: /{normalizedSlug}</p>
            </div>
          </div>
        </div>

        {/* SEO Meta Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Search className="h-5 w-5 mr-2 text-green-600" />
            SEO Meta Information
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meta Title *
                <span className={`ml-2 text-xs ${formData.metaTitle.length > 60 ? 'text-red-500' : 'text-gray-500'
                  }`}>
                  ({formData.metaTitle.length}/60)
                </span>
              </label>
              <input
                type="text"
                name="metaTitle"
                value={formData.metaTitle}
                onChange={handleInputChange}
                maxLength={60}
                className={`w-full border rounded-md px-3 py-2 focus:ring-primary focus:border-primary ${(!formData.metaTitle.trim() || formData.metaTitle.length > 60) && error ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="Professional Brand Development Services | Make My Brand"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meta Description *
                <span className={`ml-2 text-xs ${formData.metaDescription.length > 160 ? 'text-red-500' : 'text-gray-500'
                  }`}>
                  ({formData.metaDescription.length}/160)
                </span>
              </label>
              <textarea
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleInputChange}
                maxLength={160}
                rows={3}
                className={`w-full border rounded-md px-3 py-2 focus:ring-primary focus:border-primary ${(!formData.metaDescription.trim() || formData.metaDescription.length > 160) && error ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="Transform your business with our comprehensive brand development services."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Canonical URL *
              </label>
              <div className="flex space-x-2">
                <input
                  type="url"
                  name="canonicalUrl"
                  value={formData.canonicalUrl}
                  onChange={handleInputChange}
                  className={`flex-1 border rounded-md px-3 py-2 focus:ring-primary focus:border-primary ${(!formData.canonicalUrl.trim() || (formData.canonicalUrl && !isValidUrl(formData.canonicalUrl))) && error ? 'border-red-300' : 'border-gray-300'
                    }`}
                  placeholder="https://www.makemybrandlabs.com/services/brand-development"
                  required
                />
                <button
                  type="button"
                  onClick={generateCanonicalUrl}
                  disabled={isSaving}
                  className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors disabled:opacity-60"
                >
                  Generate
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media Meta */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Globe className="h-5 w-5 mr-2 text-purple-600" />
            Social Media Meta
          </h3>

          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-blue-800">Quick Setup</h4>
                  <p className="text-sm text-blue-700 mt-1">Copy your SEO meta information to social media fields.</p>
                </div>
                <button
                  type="button"
                  onClick={copyMetaToSocial}
                  className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                >
                  Copy from SEO
                </button>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Open Graph Tags</h4>
              <div className="space-y-3">
                <input
                  type="text"
                  name="ogTitle"
                  value={formData.ogTitle}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary focus:border-primary"
                  placeholder="OG Title"
                />
                <textarea
                  name="ogDescription"
                  value={formData.ogDescription}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary focus:border-primary"
                  placeholder="OG Description"
                />
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="url"
                      name="ogImage"
                      value={formData.ogImage}
                      onChange={handleInputChange}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary focus:border-primary"
                      placeholder="OG Image URL"
                    />
                    <label className={`cursor-pointer ${isSaving ? 'pointer-events-none' : ''}`}>
                      <span className={`px-3 py-2 bg-blue-600 text-white rounded-md text-sm flex items-center transition-colors ${isSaving ? 'opacity-60' : 'hover:bg-blue-700'
                        }`}>
                        <Upload className="h-4 w-4 mr-1" />
                        Upload
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleOgImageUpload}
                        className="hidden"
                        disabled={isSaving}
                      />
                    </label>
                  </div>
                  {formData.ogImage && (
                    <div className="relative inline-block">
                      <Image
                        src={formData.ogImage}
                        alt="OG Image Preview"
                        width={80}
                        height={80}
                        className="w-20 h-20 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, ogImage: '' }))}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Twitter Card</h4>
              <div className="space-y-3">
                <input
                  type="text"
                  name="twitterTitle"
                  value={formData.twitterTitle}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary focus:border-primary"
                  placeholder="Twitter Title"
                />
                <textarea
                  name="twitterDescription"
                  value={formData.twitterDescription}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary focus:border-primary"
                  placeholder="Twitter Description"
                />
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="url"
                      name="twitterImage"
                      value={formData.twitterImage}
                      onChange={handleInputChange}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary focus:border-primary"
                      placeholder="Twitter Image URL"
                    />
                    <label className={`cursor-pointer ${isSaving ? 'pointer-events-none' : ''}`}>
                      <span className={`px-3 py-2 bg-blue-600 text-white rounded-md text-sm flex items-center transition-colors ${isSaving ? 'opacity-60' : 'hover:bg-blue-700'
                        }`}>
                        <Upload className="h-4 w-4 mr-1" />
                        Upload
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleTwitterImageUpload}
                        className="hidden"
                        disabled={isSaving}
                      />
                    </label>
                  </div>
                  {formData.twitterImage && (
                    <div className="relative inline-block">
                      <Image
                        src={formData.twitterImage}
                        alt="Twitter Image Preview"
                        width={80}
                        height={80}
                        className="w-20 h-20 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, twitterImage: '' }))}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Schema Markup */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-orange-600" />
            Schema Markup (JSON-LD)
          </h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Schema Markup
              <span className="ml-2 text-xs text-gray-500">
                Optional JSON-LD structured data
              </span>
            </label>
            <textarea
              name="schemaMarkup"
              value={formData.schemaMarkup}
              onChange={handleInputChange}
              rows={8}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary font-mono text-sm"
              placeholder={`{
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Your Service Name",
  "description": "Service description",
  "provider": {
    "@type": "Organization",
    "name": "Make My Brand"
  }
}`}
            />
            <p className="mt-2 text-xs text-gray-500">
              Enter valid JSON-LD schema markup for better search engine understanding and rich snippets.
            </p>
          </div>
        </div>

        {/* Publishing Settings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-green-600" />
            Publishing Settings
          </h3>
          <div className="mb-4 text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
            <p className="font-medium mb-1">Keyboard Shortcuts:</p>
            <p>• <kbd className="px-1 py-0.5 bg-white border rounded text-xs">Ctrl+S</kbd> Save as Draft</p>
            <p>• <kbd className="px-1 py-0.5 bg-white border rounded text-xs">Ctrl+Shift+S</kbd> Publish</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
            >
              <option value="Draft">Draft</option>
              <option value="Published">Published</option>
            </select>
            {(error || success) && (
              <div className="mt-3">
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <div className="flex">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                      <div className="ml-3">
                        <p className="text-sm text-red-800">{error}</p>
                      </div>
                    </div>
                  </div>
                )}
                {success && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-3">
                    <div className="flex">
                      <CheckCircle className="h-5 w-5 text-green-400" />
                      <div className="ml-3">
                        <p className="text-sm text-green-800">{success}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={() => handleSave(false)}
                disabled={isSaving}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors disabled:opacity-60"
              >
                {isSaving ? 'Saving…' : serviceId ? 'Update Draft' : 'Save Draft'}
              </button>
              <button
                type="button"
                onClick={() => handleSave(true)}
                disabled={isSaving}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                {isSaving ? 'Publishing…' : serviceId ? 'Update & Publish' : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
